<script>
  import { createEventDispatcher } from 'svelte';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';

  export let showModal = false;
  export let preSelectedPlaces = [];

  const dispatch = createEventDispatcher();

  let allPlaces = [];
  let filteredPlaces = [];
  let selectedPlaces = []; 
  let searchQuery = '';
  let isLoading = false;
  let error = '';
  let timer;
  let wasOpen = false;

  $: if (showModal) {
    if (!wasOpen) {
      wasOpen = true;
      initialize();
    }
  } else {
    wasOpen = false;
  }

  function initialize() {
    // Ensure we work with a clean array copy
    selectedPlaces = [...preSelectedPlaces];
    searchQuery = '';
    fetchPlaces(''); 
  }

  function handleInput() {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fetchPlaces(searchQuery);
    }, 500);
  }

  async function fetchPlaces(query) {
    isLoading = true;
    error = '';
    try {
      const url = getApiUrl(`/places/search?q=${query}&countryCode=${$selectedCountry}`);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Could not load places.');
      const placeNames = await response.json();
      allPlaces = (placeNames || []).map(name => ({ name }));
      filteredPlaces = allPlaces;
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }
  
  // --- FIX: Manual Toggle Logic ---
  // This ensures selections persist even when the search results (DOM) change
  function togglePlace(name, isChecked) {
      if (isChecked) {
          if (!selectedPlaces.includes(name)) {
              selectedPlaces = [...selectedPlaces, name];
          }
      } else {
          selectedPlaces = selectedPlaces.filter(p => p !== name);
      }
  }

  function addCustomPlace() {
      if (searchQuery && !selectedPlaces.includes(searchQuery)) {
          selectedPlaces = [...selectedPlaces, searchQuery];
          searchQuery = '';
      }
  }
  
  function removeSelected(place) {
      selectedPlaces = selectedPlaces.filter(p => p !== place);
  }

  function confirmSelection() {
    dispatch('confirm', { selected: selectedPlaces });
    closeModal();
  }

  function closeModal() {
    dispatch('close');
  }
</script>

{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <h3>Select Places ({$selectedCountry})</h3>
      
      <div class="search-section">
        <div class="input-row">
            <input 
                type="text" 
                placeholder="Search or add new..." 
                bind:value={searchQuery} 
                on:input={handleInput}
            >
            <button class="add-btn" on:click={addCustomPlace} disabled={!searchQuery}>Add</button>
        </div>
      </div>
      
      <!-- Selections Area -->
      <div class="selections">
          <strong>Selected ({selectedPlaces.length}):</strong>
          <div class="chips-container">
            {#each selectedPlaces as place}
                <span class="chip">
                    {place} 
                    <button class="remove" on:click={() => removeSelected(place)}>&times;</button>
                </span>
            {/each}
          </div>
      </div>

      <!-- Search Results -->
      {#if isLoading}
        <p>Searching...</p>
      {:else}
        <div class="store-list">
          {#each filteredPlaces as place (place.name)}
            <div class="store-item">
              <label>
                  <!-- FIX: Use checked state and on:change event instead of bind:group -->
                  <input 
                    type="checkbox" 
                    value={place.name} 
                    checked={selectedPlaces.includes(place.name)}
                    on:change={(e) => togglePlace(place.name, e.target.checked)}
                  >
                  {place.name}
              </label>
            </div>
          {/each}
          
          {#if filteredPlaces.length === 0 && searchQuery}
             <p class="no-results">No existing places found. Click 'Add' to use "{searchQuery}".</p>
          {/if}
        </div>
      {/if}

      <div class="modal-actions">
        <button class="secondary" on:click={closeModal}>Cancel</button>
        <button class="primary" on:click={confirmSelection}>Confirm</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
  .modal-content { background: white; padding: 2rem; border-radius: 8px; width: 90%; max-width: 500px; max-height: 80vh; display: flex; flex-direction: column; }
  
  .input-row { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
  input[type="text"] { flex-grow: 1; padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; }
  .add-btn { padding: 0.5rem 1rem; background-color: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer; }
  
  .selections { margin-bottom: 1rem; font-size: 0.9rem; }
  .chips-container { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem; max-height: 100px; overflow-y: auto; }
  .chip { background: #e9ecef; padding: 4px 8px; border-radius: 12px; font-size: 0.9rem; display: flex; align-items: center; gap: 5px; }
  .remove { background: none; border: none; font-weight: bold; cursor: pointer; color: #dc3545; padding: 0; font-size: 1.1rem; line-height: 1; }
  
  .store-list { flex-grow: 1; overflow-y: auto; border: 1px solid #eee; padding: 0.5rem; border-radius: 4px; min-height: 150px; }
  .store-item { padding: 0.5rem; border-bottom: 1px solid #f9f9f9; }
  .store-item label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; width: 100%; }
  input[type="checkbox"] { width: 1.2rem; height: 1.2rem; margin: 0; }
  
  .modal-actions { display: flex; justify-content: flex-end; gap: 1rem; margin-top: 1rem; }
  .primary { background-color: #007bff; color: white; padding: 0.6rem 1.2rem; border-radius: 4px; border: none; cursor: pointer; }
  .secondary { background-color: #6c757d; color: white; padding: 0.6rem 1.2rem; border-radius: 4px; border: none; cursor: pointer; }
  .error { color: red; }
  .no-results { font-size: 0.9rem; color: #666; font-style: italic; padding: 0.5rem; }
</style>